import { useState, useEffect } from "react";
import SuiBox from "components/SuiBox";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import IconButton from "@mui/material/IconButton";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import UserApi from "api/userApi";
import PropTypes from "prop-types";

const ProfileEditor = ({ userId, initialData, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [userData, setUserData] = useState({ nombres: "", apellidos: "", email: "" });
  const [originalData, setOriginalData] = useState({ nombres: "", apellidos: "", email: "" }); // Store original data

  useEffect(() => {
    if (initialData) {
      setUserData({
        nombres: initialData.nombres,
        apellidos: initialData.apellidos,
        email: initialData.email,
      });
      setOriginalData({
        nombres: initialData.nombres,
        apellidos: initialData.apellidos,
        email: initialData.email,
      });
    }
  }, [initialData]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setUserData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSave = async () => {
    try {
      await UserApi.updateUser(userId, userData);
      onSave(userData); // Update and exit edit mode
      setOriginalData(userData); // Update original data after saving
      setEditing(false);
    } catch (error) {
      console.error("Error saving user data:", error);
    }
  };

  const handleCancel = () => {
    setUserData(originalData); // Reset to original data without refetching
    setEditing(false); // Exit edit mode
  };

  const handleEditClick = () => setEditing(true); // Enter edit mode

  return (
    <SuiBox position="relative" width="100%">
      {editing ? (
        <>
          <SuiBox display="flex" gap={2} mb={2} alignItems="center" position="relative">
            <SuiInput
              name="nombres"
              value={userData.nombres}
              onChange={handleInputChange}
              placeholder="Nombres"
            />
            <SuiInput
              name="apellidos"
              value={userData.apellidos}
              onChange={handleInputChange}
              placeholder="Apellidos"
            />
            <div style={{ position: "absolute", right: "-80px", top: "4px", display: "flex" }}>
              <IconButton onClick={handleSave}>
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handleCancel}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </SuiBox>
          <SuiBox display="flex" gap={2} mb={2}>
            <SuiInput
              name="email"
              value={userData.email}
              onChange={handleInputChange}
              placeholder="Correo Electrónico"
            />
          </SuiBox>
        </>
      ) : (
        <SuiBox>
          <SuiBox display="flex" alignItems="center" mb={0.5} mt={1}>
            <SuiTypography variant="h5" fontWeight="medium" lineHeight={1.2}>
              {`${userData.nombres} ${userData.apellidos}`}
            </SuiTypography>
            <IconButton
              onClick={handleEditClick}
              style={{ marginLeft: 8, padding: 0 }} // Adjust to center the icon
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </SuiBox>
          <SuiTypography variant="button" textColor="text" fontWeight="medium" mt={0.5}>
            {userData.email}
          </SuiTypography>
        </SuiBox>
      )}
    </SuiBox>
  );
};

ProfileEditor.propTypes = {
  userId: PropTypes.string.isRequired,
  initialData: PropTypes.shape({
    nombres: PropTypes.string,
    apellidos: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default ProfileEditor;
